import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Group,
  Textarea,
  ActionIcon,
  Popover,
  SimpleGrid,
  UnstyledButton,
  Text,
  Image,
  Tooltip,
  Menu,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconMoodSmile,
  IconSend,
  IconPhoto,
  IconCalendarClock,
  IconChevronDown,
  IconX,
} from "@tabler/icons-react";
import { computeSmsSegments } from "../../utils/smsSegments";
import type { MessageMediaRef } from "../../types/messaging";
import { ImageLightbox } from "./ImageLightbox";
import { ScheduleSendModal } from "./ScheduleSendModal";
import styles from "./Composer.module.css";

interface ComposerProps {
  onSend: (body: string, media?: MessageMediaRef[]) => Promise<void>;
  /** Upload one image → its stored ref (enables the attach button when set). */
  onUploadImage?: (file: File) => Promise<MessageMediaRef>;
  /** Conversation id — when set, enables the Schedule-send split button. */
  conversationId?: string;
  /** Salon timezone for scheduled-send times (required alongside conversationId). */
  timezone?: string;
  disabled?: boolean;
}

// A local preview of a staged attachment (object URL is per-viewer, revoked on
// send/remove; the durable ref is what actually gets sent).
interface PendingAttachment {
  ref: MessageMediaRef;
  previewUrl: string;
  name: string;
}

// Content types the backend (mms_media.ALLOWED_CONTENT_TYPES) accepts.
const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/gif,image/webp";

// A small curated palette — a lightweight, dependency-free emoji picker (adding
// a full emoji-picker library isn't worth the bundle/CSP cost for this).
const EMOJIS = [
  "😀",
  "😄",
  "😊",
  "😍",
  "😉",
  "😎",
  "🥳",
  "🙏",
  "👍",
  "👏",
  "🙌",
  "💪",
  "🎉",
  "✨",
  "🔥",
  "❤️",
  "😢",
  "😴",
  "🤔",
  "😅",
  "👋",
  "💇",
  "💅",
  "💆",
  "📅",
  "⏰",
  "✅",
  "❌",
  "💯",
  "☀️",
  "🌸",
  "💖",
];

export const Composer: React.FC<ComposerProps> = ({
  onSend,
  onUploadImage,
  conversationId,
  timezone,
  disabled,
}) => {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  // Full-size preview of a staged attachment (local blob URL), or null.
  const [preview, setPreview] = useState<string | null>(null);
  const [scheduleOpen, schedule] = useDisclosure(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const segments = useMemo(() => computeSmsSegments(body), [body]);
  const hasContent = body.trim().length > 0 || pending.length > 0;
  const canSend = hasContent && !sending && !uploading && !disabled;
  // Scheduling is text-only; the split button appears when a conversation is set
  // and is actionable once there's text to schedule.
  const scheduleEnabled = Boolean(conversationId);
  const canSchedule = scheduleEnabled && !disabled && body.trim().length > 0;

  const clearPending = () => {
    setPending((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
  };

  const handleSend = async () => {
    if (!canSend) return;
    const text = body;
    const media = pending.map((p) => p.ref);
    setSending(true);
    try {
      await onSend(text, media);
      setBody(""); // clear only after a successful send
      clearPending();
    } catch {
      // Keep the text + attachments so the user can retry; the thread surfaces
      // failed sends.
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file || !onUploadImage) return;
    setUploading(true);
    try {
      const ref = await onUploadImage(file);
      setPending((prev) => [
        ...prev,
        { ref, previewUrl: URL.createObjectURL(file), name: file.name },
      ]);
    } catch {
      // Upload failed — nothing staged; the user can retry.
    } finally {
      setUploading(false);
    }
  };

  const removePending = (index: number) => {
    setPending((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  return (
    <Box className={styles.composer}>
      {pending.length > 0 && (
        <Group gap="xs" className={styles.attachments}>
          {pending.map((p, i) => (
            <Box key={p.previewUrl} className={styles.attachment}>
              <UnstyledButton
                className={styles.attachmentButton}
                aria-label={`View ${p.name}`}
                onClick={() => setPreview(p.previewUrl)}
              >
                <Image
                  src={p.previewUrl}
                  alt={p.name}
                  className={styles.attachmentThumb}
                />
              </UnstyledButton>
              <ActionIcon
                size="xs"
                variant="filled"
                color="dark"
                className={styles.attachmentRemove}
                aria-label={`Remove ${p.name}`}
                onClick={() => removePending(i)}
              >
                <IconX size={12} />
              </ActionIcon>
            </Box>
          ))}
        </Group>
      )}

      <Group gap="xs" align="flex-end" wrap="nowrap">
        <Popover
          position="top-start"
          withArrow
          shadow="md"
          transitionProps={{ duration: 0 }}
        >
          <Popover.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={40}
              aria-label="Insert emoji"
              disabled={disabled}
            >
              <IconMoodSmile size={22} stroke={1.5} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown p="xs">
            <SimpleGrid cols={8} spacing={4}>
              {EMOJIS.map((emoji) => (
                <UnstyledButton
                  key={emoji}
                  className={styles.emoji}
                  aria-label={`Emoji ${emoji}`}
                  onClick={() => setBody((b) => b + emoji)}
                >
                  {emoji}
                </UnstyledButton>
              ))}
            </SimpleGrid>
          </Popover.Dropdown>
        </Popover>

        {onUploadImage && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              hidden
              onChange={handleFileChange}
              aria-hidden="true"
            />
            <Tooltip label="Attach image" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size={40}
                aria-label="Attach image"
                loading={uploading}
                disabled={disabled}
                onClick={() => fileInputRef.current?.click()}
              >
                <IconPhoto size={22} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </>
        )}

        <Box className={styles.field}>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message"
            autosize
            minRows={1}
            maxRows={6}
            disabled={disabled}
            aria-label="Message"
          />
          {body.length > 0 && (
            <Text className={styles.counter} aria-live="polite">
              {segments.length}/{segments.encoding === "GSM-7" ? 160 : 70} ·{" "}
              {segments.segments} seg{segments.segments === 1 ? "" : "s"}
            </Text>
          )}
        </Box>

        <Group gap={0} wrap="nowrap" className={styles.sendGroup}>
          <ActionIcon
            size={44}
            radius="md"
            aria-label="Send message"
            onClick={handleSend}
            loading={sending}
            disabled={!canSend}
            className={scheduleEnabled ? styles.sendMain : undefined}
            style={{
              backgroundColor: canSend ? "var(--brand-teal)" : undefined,
            }}
          >
            <IconSend size={20} />
          </ActionIcon>

          {scheduleEnabled && (
            <Menu
              position="top-end"
              withArrow
              shadow="md"
              width={210}
              radius="md"
              withinPortal
            >
              <Menu.Target>
                <ActionIcon
                  size={44}
                  radius="md"
                  aria-label="More send options"
                  disabled={!canSchedule}
                  className={styles.sendChevron}
                  style={{
                    backgroundColor: canSchedule
                      ? "var(--brand-teal)"
                      : undefined,
                  }}
                >
                  <IconChevronDown size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconCalendarClock size={16} />}
                  onClick={schedule.open}
                >
                  Schedule send…
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>

      <ImageLightbox url={preview} onClose={() => setPreview(null)} />

      {scheduleEnabled && scheduleOpen && conversationId && (
        <ScheduleSendModal
          opened
          onClose={schedule.close}
          conversationId={conversationId}
          timezone={timezone || "America/New_York"}
          initialBody={body}
          mediaStripped={pending.length > 0}
          onScheduled={() => setBody("")}
        />
      )}
    </Box>
  );
};

export default Composer;
